import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  CalendarPlus,
  Lock,
  Unlock,
  Users,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lenslopers_admin_token";

const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return "Er ging iets mis. Probeer het opnieuw.";
};

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const inputCls =
  "w-full border-b border-neutral-300 focus:border-gold outline-none py-2 bg-transparent text-sm text-ink";

const emptyEvent = {
  title: "",
  subtitle: "",
  description: "",
  image_url: "",
  price: 49,
  sale_price: "",
  shoot_minutes: 15,
  slot_minutes: 30,
  location: "Op afgesproken locatie",
  includes: "15 minuten fotografie\nminimum 2 professioneel bewerkte digitale foto's\nKeuze uit de beste foto's\n1 persoon / 1 huisdier\nOnline levering",
  extra_photo_price: 10,
};

const NewEventForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEvent);
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        subtitle: form.subtitle || null,
        description: form.description || null,
        image_url: form.image_url || null,
        price: Number(form.price),
        sale_price: form.sale_price ? Number(form.sale_price) : null,
        shoot_minutes: Number(form.shoot_minutes),
        slot_minutes: Number(form.slot_minutes),
        location: form.location,
        includes: form.includes.split("\n").map((x) => x.trim()).filter(Boolean),
        extra_photo_price: form.extra_photo_price ? Number(form.extra_photo_price) : null,
        status: "draft",
      };
      await axios.post(`${API}/admin/events`, payload, { headers: authHeaders() });
      toast.success("Event aangemaakt als verborgen — zet data open en publiceer.");
      setForm(emptyEvent);
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid="admin-event-new-toggle"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 bg-ink text-gold px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-black transition-colors"
      >
        <Plus size={14} />
        Nieuw event
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="admin-event-form"
      className="bg-beige border-l-2 border-gold p-6 md:p-8 space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <label className="block">
          <span className="eyebrow block mb-2 text-neutral-500">Titel</span>
          <input
            data-testid="admin-event-title"
            required
            value={form.title}
            onChange={update("title")}
            placeholder="Kerst mini-shoots"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2 text-neutral-500">Ondertitel</span>
          <input
            data-testid="admin-event-subtitle"
            value={form.subtitle}
            onChange={update("subtitle")}
            placeholder="15 minuten vol warmte"
            className={inputCls}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="eyebrow block mb-2 text-neutral-500">Beschrijving</span>
          <textarea
            data-testid="admin-event-description"
            rows={3}
            value={form.description}
            onChange={update("description")}
            className={`${inputCls} resize-none`}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="eyebrow block mb-2 text-neutral-500">Foto (URL)</span>
          <input
            data-testid="admin-event-image"
            value={form.image_url}
            onChange={update("image_url")}
            placeholder="https://..."
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2 text-neutral-500">Normale prijs (€)</span>
          <input
            data-testid="admin-event-price"
            required
            type="number"
            min={1}
            value={form.price}
            onChange={update("price")}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2 text-neutral-500">Actieprijs (€, optioneel)</span>
          <input
            data-testid="admin-event-sale-price"
            type="number"
            min={1}
            value={form.sale_price}
            onChange={update("sale_price")}
            placeholder="39"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2 text-neutral-500">Minuten fotograferen</span>
          <input
            data-testid="admin-event-shoot-minutes"
            required
            type="number"
            min={5}
            value={form.shoot_minutes}
            onChange={update("shoot_minutes")}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2 text-neutral-500">Minuten per tijdslot</span>
          <input
            data-testid="admin-event-slot-minutes"
            required
            type="number"
            min={5}
            value={form.slot_minutes}
            onChange={update("slot_minutes")}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2 text-neutral-500">Locatie</span>
          <input
            data-testid="admin-event-location"
            value={form.location}
            onChange={update("location")}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2 text-neutral-500">Extra foto (€)</span>
          <input
            data-testid="admin-event-extra-photo"
            type="number"
            min={0}
            value={form.extra_photo_price}
            onChange={update("extra_photo_price")}
            className={inputCls}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="eyebrow block mb-2 text-neutral-500">
            Wat is inbegrepen (één per lijn)
          </span>
          <textarea
            data-testid="admin-event-includes"
            rows={5}
            value={form.includes}
            onChange={update("includes")}
            className={`${inputCls} resize-none`}
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          data-testid="admin-event-create"
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 bg-gold text-white px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          Event aanmaken
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-neutral-500 hover:text-gold transition-colors"
        >
          Annuleren
        </button>
      </div>
    </form>
  );
};

const EventCard = ({ ev, onChanged }) => {
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [picked, setPicked] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [start, setStart] = useState("10:00");
  const [end, setEnd] = useState("16:00");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([
        axios.get(`${API}/admin/events/${ev.id}/slots`, { headers: authHeaders() }),
        axios.get(`${API}/admin/events/${ev.id}/bookings`, { headers: authHeaders() }),
      ]);
      setSlots(s.data);
      setBookings(b.data);
    } catch (err) {
      toast.error(errText(err));
    }
  }, [ev.id]);

  useEffect(() => {
    if (expanded) load();
  }, [expanded, load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
      onChanged();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const generate = () =>
    act(async () => {
      if (picked.length === 0) throw { response: { data: { detail: "Kies eerst één of meer dagen." } } };
      const { data } = await axios.post(
        `${API}/admin/events/${ev.id}/slots`,
        { dates: picked.map(toKey), start_time: start, end_time: end },
        { headers: authHeaders() }
      );
      toast.success(`${data.created} tijdslot(en) toegevoegd${data.skipped ? `, ${data.skipped} bestonden al` : ""}.`);
      setPicked([]);
    });

  const byDate = slots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});

  const openCount = slots.filter((s) => s.status === "open").length;
  const bookedCount = slots.filter((s) => s.status === "booked").length;

  return (
    <div className="bg-white border border-neutral-200" data-testid={`admin-event-${ev.slug}`}>
      <div className="p-6 md:p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-serif text-2xl text-ink leading-tight">{ev.title}</p>
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-2">
              €{(ev.sale_price || ev.price).toFixed(0)}
              {ev.sale_price ? ` (i.p.v. €${ev.price.toFixed(0)})` : ""} · {ev.shoot_minutes} min
              fotograferen · {ev.slot_minutes} min per slot
            </p>
            <p className="text-xs text-neutral-400 mt-1">/mini-shoots — {ev.slug}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 ${
                ev.status === "published" ? "bg-gold text-white" : "bg-neutral-200 text-neutral-600"
              }`}
            >
              {ev.status === "published" ? "Online" : "Verborgen"}
            </span>
            <button
              data-testid={`admin-event-toggle-status-${ev.slug}`}
              disabled={busy}
              onClick={() =>
                act(
                  () =>
                    axios.patch(
                      `${API}/admin/events/${ev.id}`,
                      { status: ev.status === "published" ? "draft" : "published" },
                      { headers: authHeaders() }
                    ),
                  ev.status === "published" ? "Event verborgen." : "Event staat online."
                )
              }
              className="inline-flex items-center gap-2 border border-ink px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-white transition-colors disabled:opacity-50"
            >
              {ev.status === "published" ? <EyeOff size={13} /> : <Eye size={13} />}
              {ev.status === "published" ? "Verbergen" : "Publiceren"}
            </button>
            <button
              data-testid={`admin-event-delete-${ev.slug}`}
              disabled={busy}
              onClick={() =>
                act(
                  () => axios.delete(`${API}/admin/events/${ev.id}`, { headers: authHeaders() }),
                  "Event verwijderd."
                )
              }
              className="inline-flex items-center gap-2 text-neutral-400 px-3 py-2 text-[11px] uppercase tracking-[0.2em] hover:text-red-700 transition-colors disabled:opacity-50"
            >
              <Trash2 size={13} />
              Verwijderen
            </button>
          </div>
        </div>

        <button
          data-testid={`admin-event-expand-${ev.slug}`}
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-2 mt-6 text-[11px] uppercase tracking-[0.2em] text-ink/70 hover:text-gold transition-colors"
        >
          <ChevronDown
            size={14}
            className={`transition-transform ${expanded ? "rotate-180" : ""}`}
          />
          {expanded ? "Sluit agenda" : "Data & tijdsloten beheren"}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-neutral-200 p-6 md:p-7 space-y-10">
          <div className="flex flex-wrap gap-6 text-xs uppercase tracking-[0.2em] text-neutral-500">
            <span>{openCount} vrij</span>
            <span className="text-gold">{bookedCount} geboekt</span>
            <span>{bookings.length} betaalde boekingen</span>
          </div>

          {/* Slot generator */}
          <div className="bg-beige p-6 border-l-2 border-gold">
            <p className="eyebrow mb-5 text-neutral-500">Dagen openzetten</p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-6">
                <div className="bg-white p-2 sm:p-3 inline-block" data-testid={`admin-slot-calendar-${ev.slug}`}>
                  <Calendar
                    mode="multiple"
                    selected={picked}
                    onSelect={(v) => setPicked(v || [])}
                    month={month}
                    onMonthChange={setMonth}
                    fromDate={new Date()}
                    classNames={{
                      day_selected:
                        "bg-gold text-white hover:bg-gold hover:text-white focus:bg-gold focus:text-white",
                    }}
                  />
                </div>
                <p className="text-xs text-neutral-500 mt-3">
                  Klik meerdere dagen aan — ze krijgen allemaal dezelfde uren.
                </p>
              </div>
              <div className="lg:col-span-6 space-y-5">
                <div className="grid grid-cols-2 gap-5">
                  <label className="block">
                    <span className="eyebrow block mb-2 text-neutral-500">Van</span>
                    <input
                      data-testid={`admin-slot-start-${ev.slug}`}
                      type="time"
                      value={start}
                      onChange={(e) => setStart(e.target.value)}
                      className={inputCls}
                    />
                  </label>
                  <label className="block">
                    <span className="eyebrow block mb-2 text-neutral-500">Tot</span>
                    <input
                      data-testid={`admin-slot-end-${ev.slug}`}
                      type="time"
                      value={end}
                      onChange={(e) => setEnd(e.target.value)}
                      className={inputCls}
                    />
                  </label>
                </div>
                <p className="text-xs text-neutral-500 leading-relaxed">
                  Er wordt automatisch elke {ev.slot_minutes} minuten een tijdslot gemaakt.
                  Bijvoorbeeld 10:00 tot 16:00 geeft {Math.floor((6 * 60) / ev.slot_minutes)}{" "}
                  sessies.
                </p>
                <button
                  data-testid={`admin-slot-generate-${ev.slug}`}
                  onClick={generate}
                  disabled={busy || picked.length === 0}
                  className="inline-flex items-center gap-2 bg-ink text-gold px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-black transition-colors disabled:opacity-50"
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <CalendarPlus size={13} />}
                  Tijdsloten aanmaken
                </button>
              </div>
            </div>
          </div>

          {/* Slots per day */}
          {Object.keys(byDate).length > 0 && (
            <div className="space-y-7" data-testid={`admin-slot-list-${ev.slug}`}>
              {Object.keys(byDate)
                .sort()
                .map((date) => (
                  <div key={date}>
                    <p className="eyebrow mb-4 text-neutral-500">{date}</p>
                    <div className="flex flex-wrap gap-2">
                      {byDate[date]
                        .sort((a, b) => a.time.localeCompare(b.time))
                        .map((s) => (
                          <div
                            key={s.id}
                            data-testid={`admin-slot-${date}-${s.time}`}
                            className={`flex items-center gap-2 px-3 py-2 text-xs border ${
                              s.status === "booked"
                                ? "bg-gold/10 border-gold text-ink"
                                : s.status === "closed"
                                ? "bg-neutral-100 border-neutral-200 text-neutral-400 line-through"
                                : s.status === "held"
                                ? "bg-beige border-neutral-200 text-neutral-600"
                                : "bg-white border-neutral-200"
                            }`}
                          >
                            <span>{s.time}</span>
                            {s.status === "booked" ? (
                              <Users size={12} className="text-gold" />
                            ) : (
                              <>
                                <button
                                  title={s.status === "closed" ? "Weer openzetten" : "Sluiten"}
                                  disabled={busy}
                                  onClick={() =>
                                    act(() =>
                                      axios.patch(
                                        `${API}/admin/slots/${s.id}`,
                                        { action: s.status === "closed" ? "open" : "close" },
                                        { headers: authHeaders() }
                                      )
                                    )
                                  }
                                  className="text-neutral-400 hover:text-gold transition-colors"
                                >
                                  {s.status === "closed" ? <Unlock size={12} /> : <Lock size={12} />}
                                </button>
                                <button
                                  title="Verwijderen"
                                  disabled={busy}
                                  onClick={() =>
                                    act(() =>
                                      axios.delete(`${API}/admin/slots/${s.id}`, {
                                        headers: authHeaders(),
                                      })
                                    )
                                  }
                                  className="text-neutral-400 hover:text-red-700 transition-colors"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Bookings */}
          <div>
            <p className="eyebrow mb-4 text-neutral-500">Betaalde boekingen</p>
            {bookings.length === 0 ? (
              <p className="text-sm text-neutral-500">Nog geen boekingen voor dit event.</p>
            ) : (
              <ul className="divide-y divide-neutral-200" data-testid={`admin-event-bookings-${ev.slug}`}>
                {bookings.map((b) => (
                  <li key={b.id} className="py-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-serif text-lg text-ink">
                        {b.slot_date} — {b.slot_time}
                      </p>
                      <p className="text-xs text-neutral-500 mt-1">
                        {b.name} · {b.email} · {b.phone}
                        {b.persons ? ` · ${b.persons} pers.` : ""}
                      </p>
                      {b.notes && <p className="text-xs text-neutral-500 mt-1">{b.notes}</p>}
                    </div>
                    <span className="text-sm text-gold">€{b.amount.toFixed(0)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/events`, { headers: authHeaders() });
      setEvents(data);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="bg-beige py-16 md:py-20 border-t border-neutral-200" data-testid="admin-events">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <p className="eyebrow mb-3">Mini-shoots</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight">
              Tijdelijke <span className="italic text-gold">events.</span>
            </h2>
            <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
              Maak een actie aan (bv. Kerst mini-shoots), zet dagen open met automatische
              tijdsloten en publiceer. Een slot wordt pas bezet zodra de klant via Stripe betaald
              heeft.
            </p>
          </div>
          <NewEventForm onCreated={load} />
        </div>

        {loading ? (
          <p className="inline-flex items-center gap-2 text-neutral-500 text-sm">
            <Loader2 size={15} className="animate-spin" /> Laden...
          </p>
        ) : events.length === 0 ? (
          <div className="bg-white border border-dashed border-neutral-300 p-14 text-center">
            <Sparkles size={26} className="text-gold mx-auto" strokeWidth={1.3} />
            <p className="font-serif text-2xl text-ink mt-6">Nog geen events</p>
            <p className="text-sm text-neutral-500 mt-2">
              Klik op "Nieuw event" om je eerste tijdelijke mini-shoot te maken.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {events.map((ev) => (
              <EventCard key={ev.id} ev={ev} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminEvents;
